/**
 * 阶段 2：3v3 席位大厅、AI 机器人编排、实时聊天室与 3 秒倒计时控制器 (Seat Lobby & Chat Controller)
 * 职责：
 * 1. 维护红方x3、蓝方x3、观战xN 席位状态，支持在线玩家入座分配与断线自动清理
 * 2. 空席位渲染为圆形 `+` 按钮，房主额外显示 `+人机` 按钮以自由组建 1v1 ~ 3v3 及 1v2 / 1v3 / 2v3 非对称阵容
 * 3. 管理席位大厅与局内共享聊天室消息流
 * 4. 房主点击开始触发 3 秒全房倒计时，3 秒内任何成员可点击取消
 */

import type {
  GameMode,
  SeatOccupant,
  RoomLobbyState,
  ChatMessageItem,
} from "../../shared/protocol";
import type { SignalingClient } from "../network/signaling";
import type { P2PChannel } from "../network/p2pChannel";
import type { SoundEngine } from "../game/soundEngine";
import type { ArenaRuntimeState } from "../state/arenaState";
import { ArenaDomRefs, escapeHtml } from "../ui/domRefs";
import type { BattlefieldView } from "../game/battlefieldView";

const BOT_NAMES = ["AI 阿尔法", "AI 贝塔", "AI 伽马", "AI 德尔塔", "AI 曜星", "AI 苍穹"];

export type SeatAction =
  | { kind: "sit"; team: "red" | "blue"; seatIndex: number }
  | { kind: "spectate" }
  | { kind: "add_bot"; team: "red" | "blue"; seatIndex: number }
  | { kind: "remove_seat"; team: "red" | "blue"; seatIndex: number }
  | { kind: "set_mode"; gameMode: GameMode };

export class SeatLobbyController {
  private countdownTimer: number | null = null;

  constructor(
    private readonly dom: ArenaDomRefs,
    private readonly state: ArenaRuntimeState,
    private readonly signaling: SignalingClient,
    private readonly p2p: P2PChannel,
    private readonly sounds: SoundEngine,
    private readonly view: BattlefieldView,
    private readonly callbacks: {
      onReturnToModePortal: () => void;
      onLaunchMatch: (lobbyState: RoomLobbyState) => void;
    }
  ) {
    this.bindEvents();
  }

  private bindEvents() {
    this.dom.elBtnSeatBack.addEventListener("click", () => {
      this.callbacks.onReturnToModePortal();
    });

    this.dom.elTabModeSpeed.addEventListener("click", () => {
      this.requestSeatAction({ kind: "set_mode", gameMode: "speed_2048" });
    });

    this.dom.elTabMode2048.addEventListener("click", () => {
      this.requestSeatAction({ kind: "set_mode", gameMode: "classic_2048" });
    });

    this.dom.elTabModeEndless.addEventListener("click", () => {
      this.requestSeatAction({ kind: "set_mode", gameMode: "endless" });
    });

    this.dom.elBtnJoinSpectator.addEventListener("click", () => {
      this.requestSeatAction({ kind: "spectate" });
    });

    // 聊天室发送 (席位大厅 & 局内聊天共用)
    const sendChatFromInput = (inputEl: HTMLInputElement) => {
      const text = inputEl.value.trim();
      if (!text) return;
      inputEl.value = "";
      const msg: ChatMessageItem = {
        id: `chat_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        senderId: this.state.myPeerId,
        senderName: this.state.myName,
        team: this.state.getMyCurrentSeatTeam(),
        text,
        timestamp: Date.now(),
      };
      this.appendChatMessage(msg);
      this.p2p.sendPacket({ type: "chat_message", msg });
    };

    this.dom.elBtnSeatChatSend.addEventListener("click", () =>
      sendChatFromInput(this.dom.elSeatChatInput)
    );
    this.dom.elSeatChatInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") sendChatFromInput(this.dom.elSeatChatInput);
    });

    this.dom.elBtnBattleChatSend.addEventListener("click", () =>
      sendChatFromInput(this.dom.elBattleChatInput)
    );
    this.dom.elBattleChatInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") sendChatFromInput(this.dom.elBattleChatInput);
    });

    // 主按钮：日常态房主点击触发 3 秒倒计时；倒计时态下全员点击同一按钮即可立即取消
    this.dom.elBtnSeatStart.addEventListener("click", () => {
      if (this.state.lobbyState.status === "countdown") {
        this.cancelStartCountdown(this.state.myName);
        this.p2p.sendPacket({
          type: "countdown_cancel",
          cancelerId: this.state.myPeerId,
          cancelerName: this.state.myName,
        });
        return;
      }

      if (!this.state.isRoomHost()) return;
      const redCount = this.state.lobbyState.redSeats.filter(Boolean).length;
      const blueCount = this.state.lobbyState.blueSeats.filter(Boolean).length;
      if (redCount === 0 || blueCount === 0) {
        alert("红方与蓝方各至少需要 1 名选手（可点击空位「+人机」添加 AI）！");
        return;
      }
      this.triggerStartCountdown(this.state.myName);
      this.p2p.sendPacket({
        type: "countdown_start",
        initiatorName: this.state.myName,
        seconds: 3,
      });
    });

    this.dom.elBtnSeatCancel.addEventListener("click", () => {
      this.cancelStartCountdown(this.state.myName);
      this.p2p.sendPacket({
        type: "countdown_cancel",
        cancelerId: this.state.myPeerId,
        cancelerName: this.state.myName,
      });
    });
  }

  public clearCountdownTimer() {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  /**
   * 房主同步房间内在线真人列表到席位表：
   * - 新进入房间的真人自动坐入第一个空席位（优先平衡红蓝人数，满6人则自动入观战席）
   * - 已离开房间的真人自动从席位或观战席移除
   */
  public reconcilePeersIntoSeats(peers: Array<{ peerId: string; name: string }>) {
    const activeIds = new Set(peers.map((p) => p.peerId));
    const isMatchActive =
      (this.state.isMatchRunning && !this.state.hasFinished) ||
      this.state.lobbyState.status === "playing";

    // 1. 移除已断线离开的真人（保留机器人；若对局进行中且选手尚在断线重连宽限期内则保留其原座位）
    for (let i = 0; i < 3; i++) {
      const r = this.state.lobbyState.redSeats[i];
      if (r && !r.isBot && !activeIds.has(r.id)) {
        const isInGrace = isMatchActive && !this.state.participants.get(r.id)?.isExited;
        if (!isInGrace) {
          this.state.lobbyState.redSeats[i] = null;
        }
      }
      const b = this.state.lobbyState.blueSeats[i];
      if (b && !b.isBot && !activeIds.has(b.id)) {
        const isInGrace = isMatchActive && !this.state.participants.get(b.id)?.isExited;
        if (!isInGrace) {
          this.state.lobbyState.blueSeats[i] = null;
        }
      }
    }
    this.state.lobbyState.spectators = this.state.lobbyState.spectators.filter((s) =>
      activeIds.has(s.id)
    );

    // 2. 为新进入且尚未入座的真人分配默认座位（若对局已在进行中且非原参战选手，则直接入观战席）
    for (const peer of peers) {
      for (const slot of this.state.lobbyState.redSeats) {
        if (slot && slot.id === peer.peerId) slot.name = peer.name;
      }
      for (const slot of this.state.lobbyState.blueSeats) {
        if (slot && slot.id === peer.peerId) slot.name = peer.name;
      }

      const alreadySeated =
        this.state.lobbyState.redSeats.some((s) => s?.id === peer.peerId) ||
        this.state.lobbyState.blueSeats.some((s) => s?.id === peer.peerId) ||
        this.state.lobbyState.spectators.some((s) => s.id === peer.peerId);

      if (alreadySeated) continue;

      if (isMatchActive && !this.state.participants.has(peer.peerId)) {
        this.state.lobbyState.spectators.push({ id: peer.peerId, name: peer.name });
        continue;
      }

      const redCount = this.state.lobbyState.redSeats.filter(Boolean).length;
      const blueCount = this.state.lobbyState.blueSeats.filter(Boolean).length;
      const preferBlue = blueCount < redCount;

      const tryOrder: Array<{ team: "red" | "blue"; idx: number }> = preferBlue
        ? [
            { team: "blue", idx: 0 },
            { team: "red", idx: 0 },
            { team: "blue", idx: 1 },
            { team: "red", idx: 1 },
            { team: "blue", idx: 2 },
            { team: "red", idx: 2 },
          ]
        : [
            { team: "red", idx: 0 },
            { team: "blue", idx: 0 },
            { team: "red", idx: 1 },
            { team: "blue", idx: 1 },
            { team: "red", idx: 2 },
            { team: "blue", idx: 2 },
          ];

      let placed = false;
      for (const slot of tryOrder) {
        const arr =
          slot.team === "red"
            ? this.state.lobbyState.redSeats
            : this.state.lobbyState.blueSeats;
        if (!arr[slot.idx]) {
          arr[slot.idx] = {
            id: peer.peerId,
            name: peer.name,
            isBot: false,
            team: slot.team,
            seatIndex: slot.idx,
          };
          placed = true;
          break;
        }
      }

      if (!placed) {
        this.state.lobbyState.spectators.push({ id: peer.peerId, name: peer.name });
      }
    }

    // 3. 若倒计时期间有选手离开导致某一方无人，自动取消倒计时
    if (this.state.lobbyState.status === "countdown") {
      const redCount = this.state.lobbyState.redSeats.filter(Boolean).length;
      const blueCount = this.state.lobbyState.blueSeats.filter(Boolean).length;
      if (redCount === 0 || blueCount === 0) {
        this.cancelStartCountdown("系统");
      }
    }
  }

  public requestSeatAction(action: SeatAction) {
    if (this.state.lobbyState.status === "countdown") return;

    if (this.state.isRoomHost()) {
      this.applySeatAction(this.state.myPeerId, this.state.myName, action);
      this.broadcastLobbyState();
      this.renderSeatLobby();
    } else {
      this.p2p.sendPacket({
        type: "seat_action_request",
        actorId: this.state.myPeerId,
        actorName: this.state.myName,
        action,
      });
    }
  }

  public applySeatAction(actorId: string, actorName: string, action: SeatAction) {
    if (action.kind === "set_mode") {
      if (actorId !== this.state.lobbyState.hostId) return;
      this.state.lobbyState.gameMode = action.gameMode;
      this.state.gameMode = action.gameMode;
      this.view.syncModePillUI();
      this.signaling.updateRoomMeta(action.gameMode);
      const label =
        action.gameMode === "endless"
          ? "无限模式"
          : action.gameMode === "speed_2048"
            ? "极速模式 (3分钟·2048)"
            : "标准模式 (8分钟·2048)";
      this.appendSystemChat(`房主将赛制切换为【${label}】`);
      return;
    }

    if (action.kind === "sit") {
      const targetArr =
        action.team === "red"
          ? this.state.lobbyState.redSeats
          : this.state.lobbyState.blueSeats;
      if (targetArr[action.seatIndex] !== null) return;

      this.removePlayerFromAllSeats(actorId);
      targetArr[action.seatIndex] = {
        id: actorId,
        name: actorName,
        isBot: false,
        team: action.team,
        seatIndex: action.seatIndex,
      };
      return;
    }

    if (action.kind === "spectate") {
      this.removePlayerFromAllSeats(actorId);
      if (!this.state.lobbyState.spectators.some((s) => s.id === actorId)) {
        this.state.lobbyState.spectators.push({ id: actorId, name: actorName });
      }
      return;
    }

    if (action.kind === "add_bot") {
      if (actorId !== this.state.lobbyState.hostId) return;
      const targetArr =
        action.team === "red"
          ? this.state.lobbyState.redSeats
          : this.state.lobbyState.blueSeats;
      if (targetArr[action.seatIndex] !== null) return;

      const botIdx = (action.team === "red" ? 0 : 3) + action.seatIndex;
      const botName = BOT_NAMES[botIdx % BOT_NAMES.length];
      targetArr[action.seatIndex] = {
        id: `bot_${action.team}_${action.seatIndex}`,
        name: botName,
        isBot: true,
        team: action.team,
        seatIndex: action.seatIndex,
      };
      return;
    }

    if (action.kind === "remove_seat") {
      if (actorId !== this.state.lobbyState.hostId) return;
      const targetArr =
        action.team === "red"
          ? this.state.lobbyState.redSeats
          : this.state.lobbyState.blueSeats;
      const occ = targetArr[action.seatIndex];
      if (!occ) return;
      targetArr[action.seatIndex] = null;
      if (!occ.isBot) {
        this.state.lobbyState.spectators.push({ id: occ.id, name: occ.name });
      }
    }
  }

  private removePlayerFromAllSeats(playerId: string) {
    for (let i = 0; i < 3; i++) {
      if (this.state.lobbyState.redSeats[i]?.id === playerId) {
        this.state.lobbyState.redSeats[i] = null;
      }
      if (this.state.lobbyState.blueSeats[i]?.id === playerId) {
        this.state.lobbyState.blueSeats[i] = null;
      }
    }
    this.state.lobbyState.spectators = this.state.lobbyState.spectators.filter(
      (s) => s.id !== playerId
    );
  }

  public broadcastLobbyState() {
    this.p2p.sendPacket({
      type: "lobby_state_sync",
      state: this.state.lobbyState,
    });
  }

  public renderSeatLobby() {
    this.state.gameMode = this.state.lobbyState.gameMode;
    this.view.syncModePillUI();

    const isHost = this.state.isRoomHost();
    const isCountdown = this.state.lobbyState.status === "countdown";

    this.dom.elTabModeSpeed.classList.toggle("active", this.state.gameMode === "speed_2048");
    this.dom.elTabMode2048.classList.toggle("active", this.state.gameMode === "classic_2048");
    this.dom.elTabModeEndless.classList.toggle("active", this.state.gameMode === "endless");
    (this.dom.elTabModeSpeed as HTMLButtonElement).disabled = !isHost || isCountdown;
    (this.dom.elTabMode2048 as HTMLButtonElement).disabled = !isHost || isCountdown;
    (this.dom.elTabModeEndless as HTMLButtonElement).disabled = !isHost || isCountdown;

    const redOccupied = this.state.lobbyState.redSeats.filter(Boolean) as SeatOccupant[];
    const blueOccupied = this.state.lobbyState.blueSeats.filter(Boolean) as SeatOccupant[];
    this.dom.elRedTeamCount.textContent = `${redOccupied.length}/3`;
    this.dom.elBlueTeamCount.textContent = `${blueOccupied.length}/3`;

    if (redOccupied.length > 0 && blueOccupied.length > 0) {
      this.dom.elSeatFormatPill.textContent = `${redOccupied.length}v${blueOccupied.length}`;
    } else {
      this.dom.elSeatFormatPill.textContent = "待入座";
    }

    const renderTeamColumn = (
      team: "red" | "blue",
      seats: Array<SeatOccupant | null>,
      container: HTMLElement
    ) => {
      container.innerHTML = seats
        .map((occ, idx) => {
          if (!occ) {
            return `
              <div class="seat-slot-card empty-slot">
                <div class="seat-empty-wrap">
                  <button class="btn-seat-join btn-sit-here" data-team="${team}" data-idx="${idx}" type="button" title="点击坐下此席位" ${
                    isCountdown ? "disabled" : ""
                  }>
                    <span class="seat-join-plus">＋</span>
                    <span class="seat-join-label">入座</span>
                  </button>
                  ${
                    isHost && !isCountdown
                      ? `<button class="btn-seat-add-bot" data-team="${team}" data-idx="${idx}" type="button" title="在此席位添加 AI 机器人">
                          <span class="seat-bot-plus">＋</span>
                          <span class="seat-bot-label">AI人机</span>
                        </button>`
                      : ""
                  }
                </div>
              </div>
            `;
          }

          const isMe = occ.id === this.state.myPeerId;
          const isSeatHost = occ.id === this.state.lobbyState.hostId;
          return `
            <div class="seat-slot-card occupied-${team}">
              <div class="seat-occupant-info">
                <span class="seat-avatar-pill">${team === "red" ? `R${idx + 1}` : `B${idx + 1}`}</span>
                <div class="seat-occupant-text">
                  <span class="seat-occupant-name">${escapeHtml(occ.name)}${isMe ? " (我)" : ""}</span>
                  <div class="seat-occupant-tags">
                    ${isSeatHost ? `<span class="seat-tag tag-host">房主</span>` : ""}
                    ${occ.isBot ? `<span class="seat-tag tag-bot">AI</span>` : ""}
                  </div>
                </div>
              </div>
              ${
                isHost && !isCountdown && (occ.isBot || !isSeatHost)
                  ? `<button class="btn-seat-remove" data-team="${team}" data-idx="${idx}" type="button" title="移除">✕</button>`
                  : ""
              }
            </div>
          `;
        })
        .join("");

      container.querySelectorAll(".btn-sit-here").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const el = e.currentTarget as HTMLElement;
          const t = el.getAttribute("data-team") as "red" | "blue";
          const idx = Number(el.getAttribute("data-idx"));
          this.requestSeatAction({ kind: "sit", team: t, seatIndex: idx });
        });
      });

      container.querySelectorAll(".btn-seat-add-bot").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const el = e.currentTarget as HTMLElement;
          const t = el.getAttribute("data-team") as "red" | "blue";
          const idx = Number(el.getAttribute("data-idx"));
          this.requestSeatAction({ kind: "add_bot", team: t, seatIndex: idx });
        });
      });

      container.querySelectorAll(".btn-seat-remove").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const el = e.currentTarget as HTMLElement;
          const t = el.getAttribute("data-team") as "red" | "blue";
          const idx = Number(el.getAttribute("data-idx"));
          this.requestSeatAction({ kind: "remove_seat", team: t, seatIndex: idx });
        });
      });
    };

    renderTeamColumn("red", this.state.lobbyState.redSeats, this.dom.elRedSeatsContainer);
    renderTeamColumn("blue", this.state.lobbyState.blueSeats, this.dom.elBlueSeatsContainer);

    if (this.state.lobbyState.spectators.length === 0) {
      this.dom.elSeatSpectatorList.innerHTML = `<span class="spectator-join-tip">暂无观战者</span>`;
    } else {
      this.dom.elSeatSpectatorList.innerHTML = this.state.lobbyState.spectators
        .map(
          (s) =>
            `<span class="spectator-pill">${escapeHtml(s.name)}${
              s.id === this.state.myPeerId ? " (你)" : ""
            }</span>`
        )
        .join("");
    }

    // 直接在「开始对决」按钮原位显示 3 秒倒计时与点击取消，不再额外弹出倒计时方块
    this.dom.elSeatCountdownBanner.style.display = "none";
    this.dom.elBtnSeatCancel.style.display = "none";
    this.dom.elBtnSeatStart.style.display = "block";

    if (isCountdown) {
      this.dom.elBtnSeatStart.disabled = false;
      this.dom.elBtnSeatStart.classList.add("is-counting-down");
      this.dom.elBtnSeatStartText.textContent = `${this.state.lobbyState.countdownSec} 秒后开局 · 点击取消`;
    } else {
      this.dom.elBtnSeatStart.classList.remove("is-counting-down");
      const readyToStart = redOccupied.length > 0 && blueOccupied.length > 0;
      if (isHost) {
        this.dom.elBtnSeatStart.disabled = !readyToStart;
        this.dom.elBtnSeatStartText.textContent = readyToStart
          ? `开始对决 (${redOccupied.length}v${blueOccupied.length})`
          : "需红蓝双方各至少 1 名选手/AI";
      } else {
        this.dom.elBtnSeatStart.disabled = true;
        this.dom.elBtnSeatStartText.textContent = "等待房主点击开始对决...";
      }
    }
  }

  public triggerStartCountdown(initiatorName: string) {
    this.clearCountdownTimer();

    this.state.lobbyState.status = "countdown";
    this.state.lobbyState.countdownSec = 3;
    this.sounds.playChargeUp();
    this.appendSystemChat(`${initiatorName} 发起了对局开始倒计时 (3秒内可取消)...`);
    this.renderSeatLobby();

    this.countdownTimer = window.setInterval(() => {
      this.state.lobbyState.countdownSec--;
      if (this.state.lobbyState.countdownSec > 0) {
        this.dom.elBtnSeatStartText.textContent = `${this.state.lobbyState.countdownSec} 秒后开局 · 点击取消`;
        this.sounds.playSlide();
      } else {
        this.clearCountdownTimer();
        if (this.state.isRoomHost()) {
          this.state.lobbyState.status = "playing";
          this.p2p.sendPacket({
            type: "match_launch",
            state: this.state.lobbyState,
          });
          this.callbacks.onLaunchMatch(this.state.lobbyState);
        }
      }
    }, 1000);
  }

  public cancelStartCountdown(cancelerName: string) {
    this.clearCountdownTimer();
    this.state.lobbyState.status = "lobby";
    this.state.lobbyState.countdownSec = 3;
    this.sounds.playBump();
    this.appendSystemChat(`${cancelerName} 取消了开始倒计时`);
    this.renderSeatLobby();
  }

  public appendSystemChat(text: string) {
    this.appendChatMessage({
      id: `sys_${Date.now()}_${Math.random()}`,
      senderId: "system",
      senderName: "系统",
      team: "system",
      text,
      timestamp: Date.now(),
    });
  }

  public appendChatMessage(msg: ChatMessageItem) {
    const html = `
      <div class="chat-msg-row">
        <span class="chat-msg-sender team-${msg.team}">[${escapeHtml(msg.senderName)}]</span>
        <span>${escapeHtml(msg.text)}</span>
      </div>
    `;
    if (this.dom.elSeatChatList) {
      this.dom.elSeatChatList.insertAdjacentHTML("beforeend", html);
      this.dom.elSeatChatList.scrollTop = this.dom.elSeatChatList.scrollHeight;
    }
    if (this.dom.elBattleChatList) {
      this.dom.elBattleChatList.insertAdjacentHTML("beforeend", html);
      this.dom.elBattleChatList.scrollTop = this.dom.elBattleChatList.scrollHeight;
    }
  }
}

